import React from 'react';
import PropTypes from 'prop-types';
import styles from './StatCircle.module.scss';

/* ────────────────────────────────────────────────────────────
 * StatCircle — small circular gauge tile for KPI summaries.
 *
 *   <StatCircle
 *     value={75}
 *     label="Completed"
 *     variant="success"
 *   />
 *
 * Pure SVG. Track + fill strokes are token-driven via a
 * `--stat-circle-fill` CSS custom property set per variant, so the
 * primitive flips automatically in dark mode and stays consistent
 * with the rest of the design system. Used by the Task Management
 * Achievements dashboard and intended for any future scorecard
 * (employee performance, project KPIs, etc.) that needs a compact
 * percent-of-target visual.
 * ──────────────────────────────────────────────────────────── */

// Normalized circumference for a circle of r=15.915 (≈ 100), so
// strokeDasharray reads directly as a percentage.
const RADIUS = 15.915;

const StatCircle = ({ value, max, variant, label, className }) => {
  const percent = Math.min(100, Math.max(0, (value / max) * 100));
  const display = Math.round(percent);

  const tileClass = [
    styles.tile,
    styles[variant],
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={tileClass}>
      <div className={styles.gauge}>
        <svg
          className={styles.svg}
          viewBox="0 0 36 36"
          role="img"
          aria-label={label ? `${label}: ${display}%` : `${display}%`}
        >
          <circle
            className={styles.track}
            cx="18"
            cy="18"
            r={RADIUS}
            fill="none"
          />
          <circle
            className={styles.fill}
            cx="18"
            cy="18"
            r={RADIUS}
            fill="none"
            // Dynamic dash from a numeric prop — the only inline style; it
            // cannot be expressed as a class because the value is per-instance.
            style={{ strokeDasharray: `${percent} ${100 - percent}` }}
          />
          <text className={styles.value} x="18" y="20.5" textAnchor="middle">
            {display}
          </text>
        </svg>
      </div>
      {label && <p className={styles.label}>{label}</p>}
    </div>
  );
};

StatCircle.propTypes = {
  value: PropTypes.number.isRequired,
  max: PropTypes.number,
  variant: PropTypes.oneOf([
    'primary',
    'success',
    'warning',
    'danger',
    'info',
    'neutral',
  ]),
  label: PropTypes.node,
  className: PropTypes.string,
};

StatCircle.defaultProps = {
  max: 100,
  variant: 'primary',
  label: null,
  className: '',
};

export default StatCircle;
